// Angular
import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
// RxJS
import { Observable, Subject, BehaviorSubject, of, empty, throwError } from 'rxjs';
import { tap, switchMap, filter, take, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { AuthResponse } from '../components/models/auth.login.response.model';
import { AuthService } from '..';
import { environment } from 'src/environments/environment';
import { ROUTES_MAP } from '../../utils/model/routes-map'; 

/**
 * More information there => https://medium.com/@MetonymyQT/angular-http-interceptors-what-are-they-and-how-to-use-them-52e060321088
 */
@Injectable()
export class InterceptService implements HttpInterceptor {
  constructor(private authService: AuthService, private router: Router,private cookieService: CookieService) {}

  private excludeInterceptors : string[] = [
    "https://warmi-payment-stg.s3.amazonaws.com",
    "https://warmi-payment-massive-process-dev.s3.amazonaws.com",
    "https://warmi-payment-web-form-dev.s3.amazonaws.com",
    "https://warmi-payment-company-extranet-dev.s3.amazonaws.com",
    "https://moonflow-public-stg.s3.amazonaws.com",
    "https://moonflow-prd.s3.amazonaws.com",
    "https://moonflow-massive-process-prd.s3.amazonaws.com",
    "https://moonflow-web-form-prd.s3.amazonaws.com",
    "https://moonflow-company-extranet-prd.s3.amazonaws.com",
    "https://warmi-payment-public-stg.s3.amazonaws.com",
    "https://moonflow-public-prd.s3.amazonaws.com",
    "https://s3.amazonaws.com/notifications.khipu.com",
    "https://moonflow-copilot-dev.s3.amazonaws.com",
    "https://moonflow-copilot-prd.s3.amazonaws.com"
  ];

  private onlyCloneRequest : string[] = [
    "https://warmi-payment-stg.s3.amazonaws.com",
    "https://warmi-payment-massive-process-dev.s3.amazonaws.com",
    "https://warmi-payment-web-form-dev.s3.amazonaws.com",
    "https://warmi-payment-company-extranet-dev.s3.amazonaws.com",
    "https://moonflow-public-stg.s3.amazonaws.com",
    "https://moonflow-prd.s3.amazonaws.com",
    "https://moonflow-massive-process-prd.s3.amazonaws.com",
    "https://moonflow-web-form-prd.s3.amazonaws.com",
    "https://moonflow-company-extranet-prd.s3.amazonaws.com",
    "https://warmi-payment-public-stg.s3.amazonaws.com",
    "https://www.googleapis.com/webfonts/v1/webfonts",
    "https://moonflow-public-prd.s3.amazonaws.com",
    "https://s3.amazonaws.com/notifications.khipu.com",
    "https://moonflow-copilot-dev.s3.amazonaws.com",
    "https://moonflow-copilot-prd.s3.amazonaws.com"
  ];

  private refreshTokenInProgress = false;
  private refreshTokenSubject: Subject<any> = new BehaviorSubject<any>(null);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = localStorage.getItem(environment.authTokenKey);
     if (!token || request.url.indexOf(ROUTES_MAP.REFRESH) > -1) {
      return next.handle(
        request.clone({
          withCredentials: true,
        }),
      ).pipe(
        tap((event: HttpEvent<any>) => {
          if (event instanceof HttpResponse) {
            if (event.headers.get(environment.refreshJwtHeader)) {
              this.authService.saveRefreshTokenInLocalstorage(event.headers.get(environment.refreshJwtHeader));
            }
          }
        })
      );
    }

    if (!this.authService.isTokenExpired()) {
      return next.handle(this.injectToken(request)).pipe(
        tap((event: HttpEvent<any>) => {
          if (event instanceof HttpResponse) {
            if (event.headers.get(environment.refreshJwtHeader)) {
              this.authService.saveRefreshTokenInLocalstorage(event.headers.get(environment.refreshJwtHeader));
            }
          }
        }),
        catchError((error: any) => {
          if (error.status == 401) {
            // El token pasó el chequeo cliente pero el servidor lo rechazó
            // (expiró en tránsito / desfase de reloj). Intentamos refrescar y
            // reintentar una sola vez antes de mandar al login.
            return this.refreshAndRetry(request, next);
          }
          if (error.status == 403) {
            this.router.navigate([ROUTES_MAP.MY_PROFILE], {
              queryParams: {},
            });
            throw new Error("FORBIDDEN");
          }
          return throwError(error)
        })
      );
    } else {
      return this.refreshAndRetry(request, next);
    }
  }

  private refreshAndRetry(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.refreshTokenInProgress) {
      this.refreshTokenInProgress = true;
      this.refreshTokenSubject.next(null);
      return this.authService.refresh().pipe(
        catchError((e : any) => {
          this.authService.clearCookiesAndLocalStorage();
          this.refreshTokenInProgress = false;
          this.router.navigateByUrl(ROUTES_MAP.LOGIN);
          this.refreshTokenSubject.error(e);
          this.refreshTokenSubject = new BehaviorSubject<any>(null);
          return empty();
        }),
        switchMap((authResponse : AuthResponse) => {
          if (!authResponse?.token || !authResponse) {
            this.refreshTokenInProgress = false;
            this.authService.clearCookiesAndLocalStorage();
            this.router.navigateByUrl(ROUTES_MAP.LOGIN);
            this.refreshTokenSubject.error(new Error('REFRESH_FAILED'));
            this.refreshTokenSubject = new BehaviorSubject<any>(null);
            return empty();
          }
          this.authService.setAuthToken(authResponse);
          this.refreshTokenInProgress = false;
          this.refreshTokenSubject.next(authResponse.token);
          return next.handle(this.injectToken(request)).pipe(
            tap((event: HttpEvent<any>) => {
              if (event instanceof HttpResponse) {
                if (event.headers.get(environment.refreshJwtHeader)) {
                  this.authService.saveRefreshTokenInLocalstorage(event.headers.get(environment.refreshJwtHeader));
                }
              }
            })
          );
        }),
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((result) => result !== null),
        take(1),
        switchMap((res) => {
          return next.handle(this.injectToken(request)).pipe(
            tap((event: HttpEvent<any>) => {
              if (event instanceof HttpResponse) {
                if (event.headers.get(environment.refreshJwtHeader)) {
                  this.authService.saveRefreshTokenInLocalstorage(event.headers.get(environment.refreshJwtHeader));
                }
              }
            })
          );
        }),
        catchError((e) => throwError(e)),
      );
    }
  }

  private injectToken(request: HttpRequest<any>) {
    const token = localStorage.getItem(environment.authTokenKey);
    if(this.onlyCloneRequest.find(x => request.url.includes(x))){
      return request.clone();
    }
    if(this.excludeInterceptors.find(x => request.url.includes(x))){
      return request.clone({
        setHeaders: {
          Timezone : Intl.DateTimeFormat().resolvedOptions().timeZone
        },
      });
    }
    return request.clone({
      withCredentials: true,
      setHeaders: {
        Authorization: `Bearer ${token}`,
        Timezone : Intl.DateTimeFormat().resolvedOptions().timeZone
      },
    });
  }
}
